import React, { useContext, useMemo, useState, useEffect } from "react";
import useAsyncEffect from "use-async-effect";
import { askAPI, RequestMethod } from "../../api/api";
import { AppContext } from "../../State/Context";
import { ActionType, BattleState } from "../../State/State";
import { Hexagen } from "../../types";
import Balatro from "../../utils/Balatro";
import { ChooseCardPopup } from "./ChooseCardPopup";
import { HandContainer } from "./HandContainer";
import { BattleStages } from "./types";
import { calculateBattleStage } from "./utils";
import { useBattleAnimation } from "./useBattleAnimation";
import { BattleInfoPanel } from "./BattleInfoPanel";
import { BattleCards } from "./BattleCards";
import "./BattleFieldPopup.css";

export const BattleFieldPopup = () => {
  const { dispatch, state } = useContext(AppContext);

  const [deck, setDeck] = useState<Hexagen[]>([]);
  const [isChooseCardPopupOpen, setIsChooseCardPopupOpen] = useState(false);
  const [battleResult, setBattleResult] = useState<BattleState | null>(null);
  const [isDefeatAnimating, setIsDefeatAnimating] = useState(false);

  const battleState = state.battle;
  const battleStage = calculateBattleStage(battleState);

  const { animationState, resetAnimation } = useBattleAnimation(battleResult);

  useEffect(() => {
    if (!battleResult || !battleState) return;

    if (
      animationState.showDefenderSecond &&
      !animationState.showDefenderThird
    ) {
      dispatch({
        type: ActionType.SET_BATTLE_STATE,
        battleState: {
          ...battleState,
          defending: battleState.defending
            ? {
                ...battleState.defending,
                secondary: battleResult.defending?.secondary || null,
              }
            : null,
        },
      });
    } else if (animationState.showDefenderThird) {
      dispatch({
        type: ActionType.SET_BATTLE_STATE,
        battleState: {
          ...battleState,
          defending: battleState.defending
            ? {
                ...battleState.defending,
                secondary: battleResult.defending?.secondary || null,
                third: battleResult.defending?.third || null,
              }
            : null,
        },
      });
    }
  }, [
    animationState.showDefenderSecond,
    animationState.showDefenderThird,
    battleResult,
  ]);

  const clickOnMainCard = () => {
    setIsChooseCardPopupOpen(true);
  };

  const onSelectCardFromHand = async (hexagenUid: string) => {
    console.log("onSelectCardFromHand: ", hexagenUid);

    if (!battleState || !battleState.attacking) return;

    const updatedHand =
      battleState.attacking.hand?.filter((uid) => uid !== hexagenUid) || null;

    if (battleStage === BattleStages.SELECT_SECONDARY_SIDE_CARD) {
      dispatch({
        type: ActionType.SET_BATTLE_STATE,
        battleState: {
          ...battleState,
          attacking: {
            ...battleState.attacking,
            secondary: hexagenUid,
            hand: updatedHand,
          },
        },
      });
    } else if (battleStage === BattleStages.SELECT_THIRD_SIDE_CARD) {
      dispatch({
        type: ActionType.SET_BATTLE_STATE,
        battleState: {
          ...battleState,
          attacking: {
            ...battleState.attacking,
            third: hexagenUid,
            hand: updatedHand,
          },
        },
      });

      const result = await askAPI<BattleState>(
        `/api/battles/release?battleUid=${battleState.uid}&secondary=${battleState.attacking.secondary}&third=${hexagenUid}`,
        RequestMethod.GET
      );

      if (!result) return;

      console.log(result?.result);
      console.log(result?.story);

      setBattleResult(result);
    }
  };

  const onSelectMainCard = async (hexagen: Hexagen) => {
    console.log("hexagenUid", hexagen.uid);
    setIsChooseCardPopupOpen(false);
    if (!battleState) return;

    const defendingMainCard = battleState.defending?.main;
    const attackerMainCard = hexagen.uid;

    if (!defendingMainCard || !attackerMainCard) return;

    dispatch({
      type: ActionType.SET_BATTLE_STATE,
      battleState: {
        ...battleState,
        attacking: battleState.attacking
          ? { ...battleState.attacking, main: hexagen.uid }
          : null,
      },
    });

    const result = await askAPI<BattleState>(
      `/api/battles/start?attackerUid=${attackerMainCard}&defenderUid=${defendingMainCard}`,
      RequestMethod.GET
    );
    if (result) {
      dispatch({ type: ActionType.SET_BATTLE_STATE, battleState: result });
    }
  };

  useAsyncEffect(async () => {
    const deck = await askAPI<Hexagen[]>(
      `/api/player/deck/${state.player?.uid}`,
      RequestMethod.GET
    );
    if (deck) {
      setDeck(deck);
    }
  }, []);

  const clickOnClose = () => {
    dispatch({ type: ActionType.HIDE_BATTLE_FIELD_POPUP });
    setBattleResult(null);
    setIsDefeatAnimating(false);
    resetAnimation();
  };

  const handleCloseWithAnimation = async () => {
    if (battleResult?.result === "lose") {
      setIsDefeatAnimating(true);
      setTimeout(() => {
        clickOnClose();
      }, 3000);
    } else if (battleResult?.result === "win" && battleState?.defending?.main) {
      clickOnClose();

      const winCardUid = battleState.defending.main;
      const hexInfo = await askAPI<Hexagen>(
        `/api/hexagen/info-by-uid?uid=${winCardUid}`,
        RequestMethod.GET
      );

      if (hexInfo) {
        dispatch({
          type: ActionType.ADD_SELECTED_HEX_INFO,
          hexInfo,
        });

        dispatch({
          type: ActionType.TOGGLE_GENERATED_AWARD_POPUP,
          coordinates: { x: hexInfo.x, y: hexInfo.y },
        });
      }
    } else {
      clickOnClose();
    }
  };

  const BG = useMemo(() => {
    return (
      <Balatro
        color1="#ffffff"
        color2="#ffffff"
        color3="#000000"
        mouseInteraction={false}
        spinSpeed={3}
      />
    );
  }, []);

  return (
    <div
      onMouseDown={clickOnClose}
      className={`appear-from-opacity-40-colored fixed inset-0 flex justify-center items-start z-[10000] ${
        isDefeatAnimating ? "grayscale transition-all duration-1000" : ""
      }`}
    >
      <div
        className={`relative w-full max-w-[480px] h-[80vh] sm:ml-8 mt-[50px] antique shadow-xl m-2 border border-black/30 outline outline-8 outline-white/20 overflow-hidden ${
          isDefeatAnimating ? "grayscale transition-all duration-1000" : ""
        }`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="w-full h-full opacity-10">{BG}</div>

        <BattleInfoPanel
          battleStage={battleStage}
          animationState={animationState}
          battleResult={battleResult}
          onClose={handleCloseWithAnimation}
        />

        <BattleCards
          battleState={battleState}
          battleStage={battleStage}
          onMainCardClick={clickOnMainCard}
          isDefeatAnimating={isDefeatAnimating}
          showBattleAnimation={animationState.showBattleAnimation}
        />

        {isChooseCardPopupOpen && (
          <ChooseCardPopup deck={deck} onSelect={onSelectMainCard} />
        )}
      </div>
      <HandContainer
        hand={battleState?.attacking?.hand}
        onSelect={onSelectCardFromHand}
      />
    </div>
  );
};
